<template>
  <ValidationObserver ref="observer" v-slot="{ invalid }">
    <v-dialog v-model="dialogComputed" persistent scrollable max-width="400px">
      <v-card>
        <v-card-title class="primary white--text py-2" primary-title>
          {{ setting.title }}
          <v-spacer />
          <Btn icon="mdi-close" @click="close" depressed iconProp color="white" />
        </v-card-title>
        <v-card-text>
          <v-container fluid class="px-1">
            <v-row>
              <v-col>
                <PartCodeInput
                  v-if="isEpson"
                  v-model="partCdComputed"
                  @change="onChangePart"
                  :disabled="isEdit"
                  :required="!isEdit"
                  validOnly
                />
                <SupplierPartInput
                  v-else
                  v-model="partCdComputed"
                  :supplierCd="''"
                  isSearchByLoginCompany
                  :disabled="isEdit"
                  :required="!isEdit"
                  validOnly
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <CompanyInput
                  :label="$t('ELE0134')"
                  v-if="isEdit || partCdComputed == ''"
                  v-model="manufacturerCdComputed"
                  disabled
                />
                <PartSupplierInput
                  :label="$t('ELE0134')"
                  v-else
                  v-model="manufacturerCdComputed"
                  :partCd="partCd"
                  :isSearchByLoginCompany="!isEpson"
                  required
                  validOnly
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <XrfGroupInput v-model="xrfGroupCdComputed" validOnly />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <Checkbox :label="$t('ELE0524')" v-model="isRequiredXrfComputed" @change="onChangeIsRequiredXrf" />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextFieldRef v-if="isRequiredXrf" :label="$t('ELE0530')" v-model="xrfInspCycleComputed" />
                <!-- 
                  int型上限だと検査入力の有効期限Datepickerが動作しない
                  IEI環境の999日が入りうる上限だったため、max_value:1000で設定
                -->
                <TextFieldInput
                  v-else
                  :label="$t('ELE0530')"
                  v-model="xrfInspCycleComputed"
                  required
                  rules="integer|min_value:0|max_value:1000"
                  right
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextFieldInput :label="$t('ELE0532')" v-model="inspQtyComputed" :maxLength="20" right required />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <DepartmentInput :label="$t('ELE0533')" v-model="inspDeptCdComputed" validOnly required />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <SelectSingleInput
                  :label="$t('ELE0534')"
                  itemText="name"
                  itemValue="value"
                  :items="xrfPostProcessingItems"
                  v-model="processCdComputed"
                  required
                />
              </v-col>
            </v-row>
            <v-row v-if="isEdit">
              <v-col>
                <BtnChooseValid v-model="isValidComputed" />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <span class="d-flex flex-row space-between">
            <BtnSave @click="onClickSave" :disabled="invalid" />
          </span>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </ValidationObserver>
</template>
<script>
import { API } from '@/plugins/api'
import { ValidationObserver } from 'vee-validate'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import DateUtils from '@/mixin/date-utils.js'
import { ModalMode, XrfPostProcessingCd } from '@/enum.js'

import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import Btn from '@/components/atoms/base/button/Btn'
import BtnSave from '@/components/atoms/button/BtnSave'
import BtnChooseValid from '@/components/atoms/button/BtnChooseValid'
import Checkbox from '@/components/atoms/base/checkbox/Checkbox'
import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'

import PartCodeInput from '@/components/atoms/select/PartCodeInput'
import CompanyInput from '@/components/atoms/select/CompanyInput'
import SupplierPartInput from '@/components/atoms/select/SupplierPartInput'
import PartSupplierInput from '@/components/atoms/select/PartSupplierInput'
import XrfGroupInput from '@/components/atoms/select/XrfGroupInput'
import DepartmentInput from '@/components/atoms/select/DepartmentInput'

export default {
  name: 'XrfBasicInfoDialog',

  mixins: [APIUtils, Message, Loading, DateUtils],

  components: {
    ValidationObserver,
    TextFieldInput,
    TextFieldRef,
    Btn,
    BtnSave,
    BtnChooseValid,
    Checkbox,
    SelectSingleInput,
    PartCodeInput,
    CompanyInput,
    SupplierPartInput,
    PartSupplierInput,
    XrfGroupInput,
    DepartmentInput,
  },

  props: {
    mode: {
      type: String,
      required: true,
    },
    dialog: {
      type: Boolean,
      required: true,
    },
    partCd: {
      type: String,
      required: true,
    },
    manufacturerCd: {
      type: String,
      required: true,
    },
    xrfGroupCd: {
      type: String,
      required: true,
    },
    isRequiredXrf: {
      type: Boolean,
      required: true,
    },
    xrfInspCycle: {
      type: String,
      required: true,
    },
    inspQty: {
      type: String,
      required: true,
    },
    inspDeptCd: {
      type: String,
      required: true,
    },
    processCd: {
      type: String,
      required: true,
    },
    isValid: {
      type: Boolean,
      required: true,
    },
    versionNo: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      settings: [
        {
          mode: ModalMode.ADD,
          title: this.$t('ELE0528'),
          url: '/master/xrfParts/register',
        },
        {
          mode: ModalMode.EDIT,
          title: this.$t('ELE0523'),
          url: '/master/xrfParts/{id}/update',
        },
      ],
    }
  },

  computed: {
    xrfPostProcessingItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(XrfPostProcessingCd).map((item) => {
          return { name: `${item} : ` + this.$t(`XrfPostProcessingCd.${item}`), value: item }
        })
      },
    },
    isEpson: {
      get() {
        return this.$store.getters.isEpson
      },
    },
    dialogComputed: {
      get() {
        return this.dialog
      },
      set(value) {
        this.$emit('update:dialog', value)
      },
    },
    partCdComputed: {
      get() {
        return this.partCd
      },
      set(value) {
        this.$emit('update:partCd', value)
      },
    },
    manufacturerCdComputed: {
      get() {
        return this.manufacturerCd
      },
      set(value) {
        this.$emit('update:manufacturerCd', value)
      },
    },
    xrfGroupCdComputed: {
      get() {
        return this.xrfGroupCd
      },
      set(value) {
        this.$emit('update:xrfGroupCd', value)
      },
    },
    isRequiredXrfComputed: {
      get() {
        return this.isRequiredXrf
      },
      set(value) {
        this.$emit('update:isRequiredXrf', value)
      },
    },
    xrfInspCycleComputed: {
      get() {
        return this.xrfInspCycle
      },
      set(value) {
        this.$emit('update:xrfInspCycle', value)
      },
    },
    inspQtyComputed: {
      get() {
        return this.inspQty
      },
      set(value) {
        this.$emit('update:inspQty', value)
      },
    },
    inspDeptCdComputed: {
      get() {
        return this.inspDeptCd
      },
      set(value) {
        this.$emit('update:inspDeptCd', value)
      },
    },
    processCdComputed: {
      get() {
        return this.processCd
      },
      set(value) {
        this.$emit('update:processCd', value)
      },
    },
    isValidComputed: {
      get() {
        return this.isValid
      },
      set(value) {
        this.$emit('update:isValid', value)
      },
    },
    setting: {
      get() {
        return this.settings.find((el) => el.mode === this.mode)
      },
    },
    isEdit: {
      get() {
        return this.setting.mode === ModalMode.EDIT
      },
    },
  },

  methods: {
    close() {
      this.dialogComputed = false
    },
    onChangePart() {
      if (this.partCd !== '') {
        // 部品CDがXボタンクリア、変更した場合
        this.manufacturerCdComputed = ''
      }
    },
    onChangeIsRequiredXrf() {
      if (!this.isRequiredXrf) {
        // 受入毎検査必須を変更した場合クリア
        this.xrfInspCycleComputed = ''
      }
    },
    async onClickSave() {
      try {
        this.startLoading()

        const body = {
          partCd: this.partCdComputed,
          manufacturerCd: this.manufacturerCdComputed,
          xrfGroupCd: this.xrfGroupCdComputed,
          isRequiredXrf: this.isRequiredXrfComputed,
          xrfInspCycle: this.isRequiredXrf ? null : this.xrfInspCycleComputed,
          inspQty: this.inspQtyComputed,
          inspDeptCd: this.inspDeptCdComputed,
          processCd: this.processCdComputed,
          isValid: this.isValidComputed,
          versionNo: this.versionNo,
        }
        await API.post(this.setting.url.replace('{id}', this.partCd), body)

        this.setSuccessMessage(this.$t('MSG0011', { p0: this.$t('ELE0523') }))
        this.close()
        this.$emit('change')
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0523') })))
      } finally {
        this.stopLoading()
      }
    },
  },
}
</script>
